import { CommonModule, Location } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, first, tap } from 'rxjs/operators';
import { RegistrationService } from 'src/app/registration/shared/registration.service';
import {
  ROUTE_DQF,
  ROUTE_DQF_ABSTRACT,
  ROUTE_DQF_DECLARE,
  ROUTE_DQF_POLICE_CERTIFICATE,
  ROUTE_DQF_PRE_EMPLOYMENT_CERTIFICATE,
  ROUTE_REPORT_INCIDENT
} from 'src/app/shared/routes';
import { StorageService } from 'src/app/shared/storage/storage.service';
import { SubSink } from 'subsink';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { SkeletonDqfReviewComponent } from './skeleton-dqf-review/skeleton-dqf-review.component';
import { DqfDriverPersonalInformationComponent } from './dqf-driver-personal-information/dqf-driver-personal-information.component';
import { DqfDriverContactInformationComponent } from './dqf-driver-contact-information/dqf-driver-contact-information.component';
import { DqfDriverEmergencyContactComponent } from './dqf-driver-emergency-contact/dqf-driver-emergency-contact.component';
import { DqfDriverLicenseInformationComponent } from './dqf-driver-license-information/dqf-driver-license-information.component';
import { DqfDriverResidenceHistoryComponent } from './dqf-driver-residence-history/dqf-driver-residence-history.component';
import { DqfDriverDrivingExperienceComponent } from './dqf-driver-driving-experience/dqf-driver-driving-experience.component';
import { DqfDriverEmploymentHistoryComponent } from './dqf-driver-employment-history/dqf-driver-employment-history.component';
import { DqfDriverAccidentRecordsComponent } from './dqf-driver-accident-records/dqf-driver-accident-records.component';
import { DqfDriverTrafficConvictionsComponent } from './dqf-driver-traffic-convictions/dqf-driver-traffic-convictions.component';
import { ViewUploadedDocumentComponent } from 'src/app/shared/components/view-uploaded-document/view-uploaded-document.component';
import {
  DOCTYPE_PERSONAL_INFO,
  DOCTYPE_LICENSE_TYPE,
  DOCTYPE_RESIDENCE_HISTORY,
  DOCTYPE_DRIVING_EXPERIENCE,
  DOCTYPE_EMP_HISTORY,
  DOCTYPE_ACCIDENT_HISTORY,
  DOCTYPE_TRAFFIC_CONVICTION,
  ALL_DRIVER_DOCS_ARRAY,
  ABSTRACT,
  POLICE_CLEARANCE,
  PRE_EMPLOYMENT_SCREENING,
  ONTARIO_DRIVING_ABSTRACT,
  DQF_DOC_STATUS,
  OPTIONAL_DRIVER_DOCS
} from '../constants';
import { DriverQualificationService } from '../service/driver-qualification.service';
import { ToastService } from 'src/app/shared/toast/toast.service';

const KEY_DRIVER = 'driver';
const DEFAULT_PROFILE_IMAGE = '/assets/images/default-profile-picture.png';
const ABSTRACT_REPORT_TYPE = 'Abstract';
const DOCTYPE_POLICE_CLEARANCE_CERTIFICATE = 'PoliceClearance';
const DOCTYPE_PRE_EMPLOYMENT_SCREENING_REPORT = 'PreEmploymentScreening';
const DOCTYPE_DRIVERS_CLEARING_HOUSE_REPORT = 'ClearingHouseReport';
const UPLOADED_STATUS = 'uploaded';
const DQF_STATUS_ACCEPTED = 'Approved';
const DQF_STATUS_PENDINGAPPROVAL = 'PendingCarrierApproval';
const DQF_STATUS_DRIVER_SIGNATURE_SUBMITTED = 'DriverSignatureSubmitted';
const DQF_STATUS_CARRIERPROCESSINGCOMPLETED = 'CarrierProcessingCompleted';
const DQF_STATUS_APPROVEDPROCESSING = 'ApprovedProcessing';
const DISABLE_TOP_LOADING_BAR = true;
const DECLARATION = 'declarationForm';
const EMPLOYMENT_HISTORY_CHECK = 'EmploymentHistoryCheck';

@Component({
    selector: 'app-dqf-review',
    templateUrl: './dqf-review.component.html',
    styleUrls: ['./dqf-review.component.scss'],
    standalone: true,
    imports: [
      CommonModule,
      MatIconModule,
      MatButtonModule,
      MatExpansionModule,
      SkeletonDqfReviewComponent,
      DqfDriverPersonalInformationComponent,
      DqfDriverContactInformationComponent,
      DqfDriverEmergencyContactComponent,
      DqfDriverLicenseInformationComponent,
      DqfDriverResidenceHistoryComponent,
      DqfDriverDrivingExperienceComponent,
      DqfDriverEmploymentHistoryComponent,
      DqfDriverAccidentRecordsComponent,
      DqfDriverTrafficConvictionsComponent,
    ViewUploadedDocumentComponent
    ]
})
export class DqfReviewComponent implements OnInit, OnDestroy {
  customCollapsedHeight: string = '36px';
  carrierCompanyName: string = null;
  profilePicture = null;
  panelOpenState = false;
  abstractDocumentId: string;
  policeClearanceId: string;
  preEmploymentId: string;
  ontarioCvorAbstractId: string;
  declationFormId: string;
  employmentHistoryCheckId: string;
  subSink = new SubSink();
  driverInfo = [];
  hideActionButtons: boolean = false;
  driverDocs = [
    { heading: 'Driver Abstract', value: 10, type: ABSTRACT },
    { heading: 'Criminal Record (Background) Check', value: 11, type: POLICE_CLEARANCE },
    { heading: 'Pre-Employment Screening', value: 12, type: PRE_EMPLOYMENT_SCREENING },
    { heading: 'Ontario CVOR Abstract', value: 13, type: ONTARIO_DRIVING_ABSTRACT },
    { heading: 'Declaration', value: 14, type: DECLARATION },
    { heading: 'Employement History Check', value: 15, type: EMPLOYMENT_HISTORY_CHECK }
  ];

  allDriverInfo = [
    { heading: 'Personal Info', value: 1, type: DOCTYPE_PERSONAL_INFO },
    { heading: 'Contact', value: 2, type: DOCTYPE_PERSONAL_INFO },
    { heading: 'Emergency Contact(s)', value: 3, type: DOCTYPE_PERSONAL_INFO },
    { heading: 'License Info', value: 4, type: DOCTYPE_LICENSE_TYPE },
    { heading: 'Residence History', value: 5, type: DOCTYPE_RESIDENCE_HISTORY },
    { heading: 'Driving Experience', value: 6, type: DOCTYPE_DRIVING_EXPERIENCE },
    { heading: 'Employment History', value: 7, type: DOCTYPE_EMP_HISTORY },
    { heading: 'Accident Records', value: 8, type: DOCTYPE_ACCIDENT_HISTORY },
    { heading: 'Traffic Convictions', value: 9, type: DOCTYPE_TRAFFIC_CONVICTION }
  ];

  profileInfo = this.allDriverInfo;
  isContentReady: boolean = false;
  dqf: any;
  profileInfoVersions: any = {
    'Personal Info': 0,
    Contact: 0,
    'Emergency Contact(s)': 0,
    'License Info': 0,
    'Residence History': 0,
    'Driving Experience': 0,
    'Employment History': 0,
    'Accident Records': 0,
    'Traffic Convictions': 0
  };
  isVersionsLoaded: boolean = false;
  driverVersionDetails: any;
  dqfId: string;
  readonly POLICE_CLEARANCE = POLICE_CLEARANCE;
  readonly PRE_EMPLOYMENT_SCREENING = PRE_EMPLOYMENT_SCREENING;

  constructor(
    private location: Location,
    private registrationService: RegistrationService,
    private router: Router,
    private readonly route: ActivatedRoute,
    private driverQualificationService: DriverQualificationService,
    private storageService: StorageService,
    private readonly toastService: ToastService,
    @Inject('environmentData') private environment: any
  ) {}

  ngOnInit(): void {
    this.dqfId = this.route.snapshot.paramMap.get('id');
    this.carrierCompanyName = this.driverQualificationService.dqfData?.carrierCompanyName;

    this.subSink.add(
      this.registrationService.loadDriver(DISABLE_TOP_LOADING_BAR).subscribe(() => {
        this.loadDocumentIds();
      })
    );
    this.loadDQFs();
    this.loadData();
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  backAction(): void {
    this.location.back();
  }

  navigateToSign(): void {
    this.router.navigate([`${ROUTE_DQF}/${ROUTE_DQF_DECLARE}/${this.dqfId}`]);
  }
  checkDQFStatus(): void {
    if (
      [
        DQF_STATUS_ACCEPTED,
        DQF_STATUS_PENDINGAPPROVAL,
        DQF_STATUS_CARRIERPROCESSINGCOMPLETED,
        DQF_STATUS_APPROVEDPROCESSING
      ].includes(this.dqf.status)
    ) {
      this.hideActionButtons = true;
      this.profileInfoVersions = {
        'Personal Info': this.getVersionFromDQF(DOCTYPE_PERSONAL_INFO),
        Contact: this.getVersionFromDQF(DOCTYPE_PERSONAL_INFO),
        'Emergency Contact(s)': this.getVersionFromDQF(DOCTYPE_PERSONAL_INFO),
        'License Info': this.getVersionFromDQF(DOCTYPE_LICENSE_TYPE),
        'Residence History': this.getVersionFromDQF(DOCTYPE_RESIDENCE_HISTORY),
        'Driving Experience': this.getVersionFromDQF(DOCTYPE_DRIVING_EXPERIENCE),
        'Employment History': this.getVersionFromDQF(DOCTYPE_EMP_HISTORY),
        'Accident Records': this.getVersionFromDQF(DOCTYPE_ACCIDENT_HISTORY),
        'Traffic Convictions': this.getVersionFromDQF(DOCTYPE_TRAFFIC_CONVICTION)
      };
      this.subSink.add(
        this.registrationService
          .loadDriver(DISABLE_TOP_LOADING_BAR, this.profileInfoVersions['Personal Info'])
          .subscribe(data => {
            this.driverVersionDetails = data;
            this.isVersionsLoaded = true;
          })
      );
    } else {
      this.isVersionsLoaded = true;
    }
  }

  loadData(): void {
    this.getDriverProfilePicture();
    this.loadDocumentIds();
  }

  private loadDocumentIds(): void {
    this.getDocumentInfo(ABSTRACT_REPORT_TYPE, 'abstractDocumentId');
    this.getDocumentInfo(DOCTYPE_POLICE_CLEARANCE_CERTIFICATE, 'policeClearanceId');
    this.getDocumentInfo(DOCTYPE_PRE_EMPLOYMENT_SCREENING_REPORT, 'preEmploymentId');
    this.getDocumentInfo(ONTARIO_DRIVING_ABSTRACT, 'ontarioCvorAbstractId');
    this.getDocumentInfo(DECLARATION, 'declationFormId');
    this.getDocumentInfo(EMPLOYMENT_HISTORY_CHECK, 'employmentHistoryCheckId');
  }

  private getDriverProfilePicture(): void {
    this.isContentReady = false;
    this.subSink.sink = this.registrationService.loadProfileImageThumbnail(DISABLE_TOP_LOADING_BAR).subscribe(
      response => {
        if (response) {
          this.profilePicture = response.fileUrl;
        } else {
          this.profilePicture = DEFAULT_PROFILE_IMAGE;
        }
        this.driverQualificationService.profilePicture = this.profilePicture;
        this.isContentReady = true;
      },
      error => {
        this.isContentReady = true;
        this.profilePicture = DEFAULT_PROFILE_IMAGE;
        this.driverQualificationService.profilePicture = this.profilePicture;
      }
    );
  }

  private getDocumentInfo(reportType: string, documentTypeId: string): void {
    const driver = this.storageService.find(KEY_DRIVER);
    if (driver && driver.notification) {
      const filteredValue = driver.notification.filter(
        notification =>
          notification.type === reportType &&
          notification.status == UPLOADED_STATUS &&
          notification.dqfId === this.dqfId
      );
      if (filteredValue.length) {
        this[documentTypeId] = filteredValue[0].id;
      }
    }
  }

  onSectionClick(info: any): void {
    if (this.isAbstractSection(info) && this.shouldOpenDocumentUpload(info)) {
      this.navigateToAbstractUpload();
      return;
    }
    this.toggleCard(info);
  }

  onUploadDocumentClick(event: Event, info: { value?: number; type?: string }): void {
    event.stopPropagation();
    if (this.isPccSection(info)) {
      this.navigateToPccUpload();
      return;
    }
    if (this.isPspSection(info)) {
      this.navigateToPspUpload();
    }
  }

  toggleCard(info: any) {
    info.expanded = !info.expanded;
  }

  getSectionIcon(value: number): string {
    const iconMap = {
      1: 'profile-info-icon',         // Personal Info
      2: 'profile-info-icon',         // Contact
      3: 'profile-info-icon',         // Emergency Contact(s)
      4: 'license-info-icon',         // License Info
      5: 'residence-icon',            // Residence History
      6: 'driving-exp-icon',          // Driving Experience
      7: 'emp-history-icon',          // Employment History
      8: 'accident-record-icon',      // Accident Records
      9: 'accident-record-icon',      // Traffic Convictions
      10: 'accident-record-icon',     // Driver Abstract
      11: 'car-icon',                 // Criminal Record (Background) Check
      12: 'zoom-icon',                // Pre-Employment Screening
      13: 'accident-record-icon',     // Ontario CVOR Abstract
      14: 'emp-history-icon',         // Declaration
      15: 'emp-history-icon'          // Employment History Check
    };
    return iconMap[value] || 'folder-icon';
  }


  getSectionStatus(info: any): string {
    // Based on the screenshots, first 9 sections are completed, documents are not started
    // This should be updated based on actual completion logic from the DQF data
    if (info.value <= 9) {
      return 'completed'; // Personal info through Traffic Convictions are completed
    }
    
    // For driver documents (values 10-15), check if they exist or are carrier upload requested
    if (info.value >= 10 && info.value <= 15) {
      return this.getDriverDocumentStatus(info.type);
    }
    
    return 'not_started'; // Other sections are not started
  }

  private getDriverDocumentStatus(documentType: string): string {
    const attachedDoc = this.getAttachedDocument(documentType);

    if (attachedDoc?.status === DQF_DOC_STATUS.REJECTED) {
      return 'action_required';
    }

    // Carrier upload request takes priority so PCC/PSP match Abstract / Ontario CVOR.
    if (this.isCarrierUploadRequestedStatus(attachedDoc)) {
      return 'carrier_upload_requested';
    }

    // After DQF submission, optional PCC/PSP are either fully uploaded or NOT COMPLETED.
    // Do not treat a stale uploaded notification as COMPLETED when the attached doc is incomplete.
    if (this.isOptionalDriverDocument(documentType) && this.isDqfSubmitted()) {
      if (this.isOptionalDocumentUploadedAndComplete(attachedDoc, documentType)) {
        return 'completed';
      }
      return 'not_completed';
    }

    if (this.isOptionalDriverDocumentSkipped(attachedDoc, documentType)) {
      return 'not_completed';
    }

    if (attachedDoc?.status === DQF_DOC_STATUS.PENDING && attachedDoc?.isRequirementsCompleted === false) {
      return 'action_required';
    }

    const driver = this.storageService.find(KEY_DRIVER);
    if (driver && driver.notification) {
      const document = driver.notification.find(
        notification =>
          notification.type === documentType &&
          notification.status === UPLOADED_STATUS &&
          notification.dqfId === this.dqfId
      );
      if (document) {
        return 'completed';
      }
    }

    return 'not_started';
  }

  private isCarrierUploadRequestedStatus(attachedDoc: { status?: string } | undefined): boolean {
    return attachedDoc?.status === DQF_DOC_STATUS.CARRIER_UPLOAD_REQUESTED;
  }

  private isOptionalDocumentUploadedAndComplete(
    attachedDoc: { status?: string; isRequirementsCompleted?: boolean } | undefined,
    documentType: string
  ): boolean {
    if (!attachedDoc || !this.hasUploadedDriverDocument(documentType)) {
      return false;
    }

    const isCompletedStatus =
      attachedDoc.status === DQF_DOC_STATUS.DRIVER_REVIEWED ||
      attachedDoc.status === DQF_DOC_STATUS.DRIVER_SIGNED ||
      attachedDoc.status === DQF_DOC_STATUS.ACCEPTED;

    return isCompletedStatus && attachedDoc.isRequirementsCompleted === true;
  }

  private isOptionalDriverDocument(documentType?: string): boolean {
    return !!documentType && (OPTIONAL_DRIVER_DOCS as readonly string[]).includes(documentType);
  }

  private isOptionalDriverDocumentSkipped(
    attachedDoc: { status?: string; isRequirementsCompleted?: boolean } | undefined,
    documentType: string
  ): boolean {
    return (
      this.isOptionalDriverDocument(documentType) &&
      (!attachedDoc ||
        (attachedDoc.status === DQF_DOC_STATUS.PENDING && attachedDoc.isRequirementsCompleted !== true))
    );
  }

  private isDqfSubmitted(): boolean {
    return [
      DQF_STATUS_ACCEPTED,
      DQF_STATUS_PENDINGAPPROVAL,
      DQF_STATUS_DRIVER_SIGNATURE_SUBMITTED,
      DQF_STATUS_CARRIERPROCESSINGCOMPLETED,
      DQF_STATUS_APPROVEDPROCESSING
    ].includes(this.dqf?.status);
  }

  private isAbstractSection(info: { value?: number; type?: string }): boolean {
    return info?.value === 10 || info?.type === ABSTRACT;
  }

  private isPccSection(info: { value?: number; type?: string }): boolean {
    return info?.value === 11 || info?.type === POLICE_CLEARANCE;
  }

  private isPspSection(info: { value?: number; type?: string }): boolean {
    return info?.value === 12 || info?.type === PRE_EMPLOYMENT_SCREENING;
  }

  private hasUploadedDriverDocument(documentType: string): boolean {
    if (documentType === ABSTRACT) {
      return !!this.abstractDocumentId;
    }
    if (documentType === POLICE_CLEARANCE) {
      return !!this.policeClearanceId;
    }
    if (documentType === PRE_EMPLOYMENT_SCREENING) {
      return !!this.preEmploymentId;
    }
    return false;
  }

  private shouldOpenDocumentUpload(info: { value?: number; type?: string }): boolean {
    // Carrier already requested: expand the section with the pending message (do not open upload).
    if (info?.type && this.isCarrierUploadRequested(info.type)) {
      return false;
    }

    // PCC/PSP open upload from the expanded panel Upload button, not on row click.
    if (this.isAbstractSection(info)) {
      return this.getSectionStatus(info) === 'action_required';
    }
    return false;
  }

  getAttachedDocument(documentType: string): any {
    return this.dqf?.attachedDocuments?.find(doc => doc.type === documentType);
  }

  isCarrierUploadRequested(documentType: string): boolean {
    return this.isCarrierUploadRequestedStatus(this.getAttachedDocument(documentType));
  }

  getUploadedDocumentId(info: { value: number }): string | undefined {
    if (info.value === 11) {
      return this.policeClearanceId;
    }
    if (info.value === 12) {
      return this.preEmploymentId;
    }
    return undefined;
  }

  isOptionalDocumentNotUploaded(info: { value: number; type?: string }): boolean {
    if (info.value !== 11 && info.value !== 12) {
      return false;
    }
    return !this.getUploadedDocumentId(info) && !this.isCarrierUploadRequested(info.type);
  }

  isCarrierUploadPendingSection(info: { type?: string }): boolean {
    return !!info?.type && this.isCarrierUploadRequested(info.type);
  }

  needsDriverReupload(documentType: string): boolean {
    const attachedDoc = this.getAttachedDocument(documentType);
    if (!attachedDoc) {
      return false;
    }

    // Driver asked the carrier to upload; this is not a removed-document state.
    if (this.isCarrierUploadRequestedStatus(attachedDoc)) {
      return false;
    }

    if (attachedDoc.status === DQF_DOC_STATUS.REJECTED) {
      return true;
    }

    if (this.hasUploadedDriverDocument(documentType)) {
      return false;
    }

    // After DQF submission, a missing optional PCC/PSP file means the carrier
    // removed it. Prompt reupload on the same card without ACTION REQUIRED.
    if (this.isOptionalDriverDocument(documentType) && this.isDqfSubmitted()) {
      return (
        attachedDoc.status === DQF_DOC_STATUS.PENDING ||
        attachedDoc.isRequirementsCompleted !== true
      );
    }

    // Optional PSP/PCC left unuploaded before submission should not prompt reupload
    if (this.isOptionalDriverDocumentSkipped(attachedDoc, documentType)) {
      return false;
    }

    return (
      attachedDoc.status === DQF_DOC_STATUS.PENDING ||
      attachedDoc.isRequirementsCompleted === false
    );
  }

  getReuploadMessage(_info: { value?: number; type?: string }): string | null {
    // Do not show "document was removed" hints on the review list — they conflict with
    // carrier-upload REQUESTED state and confuse drivers. Navigation to re-upload still
    // uses needsDriverReupload().
    return null;
  }

  navigateToAbstractUpload(): void {
    this.driverQualificationService.dqfId = this.dqfId;
    this.driverQualificationService.dqfData = this.dqf;
    this.router.navigate([`${ROUTE_DQF}/${ROUTE_DQF_ABSTRACT}`]);
  }

  navigateToPccUpload(): void {
    this.driverQualificationService.dqfId = this.dqfId;
    this.driverQualificationService.dqfData = this.dqf;
    this.router.navigate([`${ROUTE_DQF}/${ROUTE_DQF_POLICE_CERTIFICATE}`]);
  }

  navigateToPspUpload(): void {
    this.driverQualificationService.dqfId = this.dqfId;
    this.driverQualificationService.dqfData = this.dqf;
    this.router.navigate([`${ROUTE_DQF}/${ROUTE_DQF_PRE_EMPLOYMENT_CERTIFICATE}`]);
  }

  getStatusText(info: any): string {
    const status = this.getSectionStatus(info);
    switch (status) {
      case 'completed':
        return 'COMPLETED';
      case 'carrier_upload_requested':
        return 'REQUESTED CARRIER';
      case 'action_required':
        return 'ACTION REQUIRED';
      case 'not_completed':
        return 'NOT COMPLETED';
      case 'not_started':
      default:
        return 'NOT STARTED';
    }
  }

  getStatusIcon(info: any): string {
    const status = this.getSectionStatus(info);
    switch (status) {
      case 'completed':
        return 'check_circle';
      case 'carrier_upload_requested':
        return 'schedule';
      case 'action_required':
        return 'error_outline';
      case 'not_completed':
        return 'check_circle';
      case 'not_started':
      default:
        return 'radio_button_unchecked';
    }
  }

  getStatusClass(info: any): string {
    const status = this.getSectionStatus(info);
    switch (status) {
      case 'completed':
        return 'status-completed';
      case 'carrier_upload_requested':
        return 'status-pending';
      case 'action_required':
        return 'status-action-required';
      case 'not_completed':
        return 'status-not-completed';
      case 'not_started':
      default:
        return 'status-incomplete';
    }
  }

  private loadDQFs() {
    this.driverQualificationService
      .getDqfById(this.dqfId)
      .pipe(
        first(),
        tap(response => {
          if (response) {
            this.dqf = response;
            this.addDocumentsToDisplay(this.dqf);
            this.checkDQFStatus();
          }
        }),
        catchError((error: any) => {
          this.toastService.showError(error.message);
          return error;
        })
      )
      .subscribe();
  }

  addDocumentsToDisplay(documents: any) {
    documents?.attachedDocuments?.forEach(element => {
      if (ALL_DRIVER_DOCS_ARRAY.includes(element.type)) {
        const filteredValue = this.driverDocs.filter(e => e.type === element.type);
        if (filteredValue.length) {
          this.profileInfo.push(filteredValue[0]);
        }
      }
    });
  }

  getVersionFromDQF(type) {
    return this.dqf.attachedDocuments.find(el => el.type === type)?.version ?? 0;
  }

}
